import { useMultiColumnAutoCompleteStore } from '@modules/common/components/MultiColumnAutoComplete/hooks';
import { MultiColumnAutoCompleteActionType } from '@modules/common/components/MultiColumnAutoComplete/hooks/useMultiColumnAutoCompleteStore/multiColumnAutoCompleteActionType';
import {
  alpha,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  BoxProps,
  Typography,
} from '@mui/material';
import { SyntheticEvent, useEffect, useRef } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

interface OptionProps<T> {
  index: number;
  rowIndex: number;
  columnIndex: number;
  option: T;
  onChange?: (
    event: SyntheticEvent<Element, Event>,
    value: T,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T> | undefined,
  ) => void;
}

export function Option<T>({
  option,
  rowIndex,
  columnIndex,
  index,
  onChange,
}: OptionProps<T>) {
  const dispatch = useMultiColumnAutoCompleteStore(({ dispatch }) => dispatch);
  const renderOption = useMultiColumnAutoCompleteStore(
    ({ renderOption }) => renderOption,
  );
  const activeRowIndex = useMultiColumnAutoCompleteStore(
    ({ activeRowIndex }) => activeRowIndex,
  );
  const activeColumnIndex = useMultiColumnAutoCompleteStore(
    ({ activeColumnIndex }) => activeColumnIndex,
  );
  const activeOptionIndex = useMultiColumnAutoCompleteStore(
    ({ activeOptionIndex }) => activeOptionIndex,
  );

  const isActive =
    activeRowIndex === rowIndex &&
    activeColumnIndex === columnIndex &&
    activeOptionIndex === index;

  const optionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isActive && optionRef.current) {
      scrollIntoView(optionRef.current, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [isActive, optionRef]);

  const selectOption = (event: SyntheticEvent) => {
    onChange?.(event, option, 'selectOption');
  };

  const activateOption = () => {
    dispatch({
      type: MultiColumnAutoCompleteActionType.SET,
      payload: {
        activeRowIndex: rowIndex,
        activeColumnIndex: columnIndex,
        activeOptionIndex: index,
      },
    });
  };

  const optionContainerProps = {
    onClick: selectOption,
    onMouseEnter: activateOption,
    ref: optionRef,
    role: 'option',
  };

  const optionProps: BoxProps = {
    className: isActive ? 'Mui-focused' : '',
    sx: {
      pl: 2,
      py: 0.75,
      cursor: 'pointer',
      backgroundColor: (theme) =>
        isActive ? alpha(theme.palette.text.primary, 0.08) : undefined,
    },
  };

  if (renderOption) {
    return (
      <Box {...optionContainerProps}>{renderOption(optionProps, option)}</Box>
    );
  }

  return (
    <Box {...optionContainerProps} {...optionProps}>
      <Typography variant="subtitle2" fontWeight={400}>
        {String(option)}
      </Typography>
    </Box>
  );
}
